import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import { CookiePreferencesButton } from "./cookies/CookiePreferencesButton";
import reklaimIcon from "@/assets/reklaim-icon.png";
import {
  FOOTER_COLUMNS,
  FOOTER_LEGAL_BAR_ITEMS,
  FOOTER_SOCIAL_LINKS,
  FOOTER_TICKERS,
  FOOTER_TOP_NAV,
  type FooterHref,
} from "@/lib/footerLinks";

function FooterAnchor({
  label,
  href,
  className,
  children,
  ariaLabel,
}: {
  label: string;
  href: FooterHref;
  className?: string;
  children?: ReactNode;
  ariaLabel?: string;
}) {
  const content = children ?? label;
  if (href.kind === "external") {
    return (
      <a
        href={href.href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        aria-label={ariaLabel ?? label}
      >
        {content}
      </a>
    );
  }

  return (
    <Link to={href.href} className={className} aria-label={ariaLabel ?? label}>
      {content}
    </Link>
  );
}

function TopNavLink({ label, href }: { label: string; href: FooterHref }) {
  return (
    <FooterAnchor
      label={label}
      href={href}
      className="text-sm font-medium text-foreground hover:text-foreground/80 transition-colors whitespace-nowrap"
    />
  );
}

function ColumnLink({ label, href }: { label: string; href: FooterHref }) {
  return (
    <FooterAnchor
      label={label}
      href={href}
      className="group flex items-center justify-between gap-3 text-sm text-foreground hover:text-foreground/80 transition-colors"
    >
      <span>{label}</span>
      <ArrowRight
        className="h-4 w-4 shrink-0 text-yellow-500 transition-transform group-hover:translate-x-0.5"
        aria-hidden="true"
      />
    </FooterAnchor>
  );
}

export const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <footer className="mt-8 border-t border-border bg-background">
      <div className="container mx-auto px-6">
        {/* Top nav row */}
        <div className="flex flex-col gap-6 border-b border-border py-8 lg:flex-row lg:items-center lg:justify-between">
          <Link
            to="/"
            className="flex shrink-0 items-center gap-3"
            aria-label="Reklaim home"
          >
            <img
              src={reklaimIcon}
              alt=""
              aria-hidden="true"
              className="h-9 w-9"
              loading="lazy"
            />
            <span className="text-2xl font-bold tracking-tight text-foreground">
              Reklaim
            </span>
          </Link>

          <nav
            className="flex flex-wrap items-center gap-x-6 gap-y-3 lg:justify-center"
            aria-label="Footer primary"
          >
            {FOOTER_TOP_NAV.map((item) => (
              <TopNavLink key={item.label} label={item.label} href={item.href} />
            ))}
          </nav>

          <div className="flex items-center gap-4 lg:justify-end">
            {FOOTER_SOCIAL_LINKS.map((social) => {
              const Icon = social.icon;
              return (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-foreground hover:text-foreground/70 transition-colors"
                  aria-label={social.label}
                >
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </a>
              );
            })}
          </div>
        </div>

        {/* Four-column grid. All columns are static link sets defined in
            `src/lib/footerLinks.ts`. RESOURCES + FOR BUSINESSES use relative
            (`kind: "internal"`) paths so React Router resolves them against
            the current origin — the same href works in dev (localhost),
            staging (beta.reklaimyou.com), and production (www.reklaimyou.com)
            without per-env code. See the docblock at the top of
            `footerLinks.ts` for the env-respecting URL contract. */}
        <div className="grid grid-cols-1 gap-10 border-b border-border py-10 sm:grid-cols-2 lg:grid-cols-4">
          {FOOTER_COLUMNS.map((column) => {
            const Icon = column.icon;
            return (
              <div key={column.id}>
                <div className="mb-4 flex items-center gap-2">
                  <Icon className="h-5 w-5 text-yellow-500" aria-hidden="true" />
                  <h2 className="text-xs font-semibold tracking-wider text-muted-foreground">
                    {column.title}
                  </h2>
                </div>
                <ul className="space-y-3">
                  {column.links.map((link) => (
                    <li key={link.label}>
                      <ColumnLink label={link.label} href={link.href} />
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Bottom legal bar */}
        <div className="flex flex-col gap-4 py-6 text-xs text-muted-foreground md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>© {year} Reklaim Inc.</span>
            {FOOTER_TICKERS.map((ticker) => (
              <span key={ticker} className="flex items-center gap-2">
                <span className="text-muted-foreground/40" aria-hidden="true">
                  |
                </span>
                <span>{ticker}</span>
              </span>
            ))}
          </div>

          {/* Bottom legal bar — iterates FOOTER_LEGAL_BAR_ITEMS in order and
              inserts `|` separators between items. Re-ordering, adding, or
              removing entries (including Cookie Settings) is a data-only
              change in `footerLinks.ts`; the renderer does NOT special-case
              any item by label or index. */}
          <nav
            className="flex flex-wrap items-center gap-x-3 gap-y-2"
            aria-label="Footer legal"
          >
            {FOOTER_LEGAL_BAR_ITEMS.map((item, idx) => (
              <span key={item.label} className="flex items-center gap-3">
                {item.kind === "link" ? (
                  <FooterAnchor
                    label={item.label}
                    href={item.href}
                    className="hover:text-foreground transition-colors"
                  />
                ) : (
                  <CookiePreferencesButton className="hover:text-foreground transition-colors cursor-pointer">
                    {item.label}
                  </CookiePreferencesButton>
                )}
                {idx < FOOTER_LEGAL_BAR_ITEMS.length - 1 && (
                  <span className="text-muted-foreground/30" aria-hidden="true">
                    |
                  </span>
                )}
              </span>
            ))}
          </nav>
        </div>
      </div>
    </footer>
  );
};
